/**
 * Panneau : catalogue LLM du workspace (owner/admin). Onglet des paramètres workspace.
 */
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { api } from '../lib/api'
import { useAppLanguage } from '../i18n'

type CatalogModel = {
  id: string
  description?: string | null
  enabled: boolean
  sort_order: number
  size?: number | null
  modified_at?: unknown
}

type CatalogProvider = {
  id: string
  models: CatalogModel[]
  has_api_key?: boolean
}

type CatalogSnapshot = {
  providers: CatalogProvider[]
}

function flattenOffers(providers: CatalogProvider[]) {
  return providers.flatMap(p =>
    p.models.map(m => ({
      provider: p.id,
      model_id: m.id,
      enabled: m.enabled,
      sort_order: m.sort_order,
    }))
  )
}

export default function WorkspaceLlmCatalogPanel() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const { t } = useAppLanguage()
  const lc = t.agents.llmCatalog
  const wid = workspaceId ? Number(workspaceId) : NaN

  const [snapshot, setSnapshot] = useState<CatalogSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!Number.isFinite(wid)) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get<CatalogSnapshot>(`/workspaces/${wid}/llm-catalog`)
      setSnapshot(data)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: { code?: string } } } }
      const code = ax?.response?.data?.detail?.code
      if (code === 'LLM_CATALOG_FORBIDDEN') setError(lc.errForbidden)
      else setError(lc.loadError)
      setSnapshot(null)
    } finally {
      setLoading(false)
    }
  }, [wid, lc.loadError, lc.errForbidden])

  useEffect(() => {
    load()
  }, [load])

  const updateModel = (
    providerId: string,
    modelId: string,
    patch: Partial<Pick<CatalogModel, 'enabled' | 'sort_order'>>
  ) => {
    setSnapshot(prev => {
      if (!prev) return prev
      return {
        providers: prev.providers.map(p => {
          if (p.id !== providerId) return p
          return {
            ...p,
            models: p.models.map(m => (m.id === modelId ? { ...m, ...patch } : m)),
          }
        }),
      }
    })
  }

  const save = async () => {
    if (!snapshot || !Number.isFinite(wid)) return
    setSaving(true)
    setError(null)
    try {
      const { data } = await api.put<CatalogSnapshot>(`/workspaces/${wid}/llm-catalog`, {
        offers: flattenOffers(snapshot.providers),
      })
      setSnapshot(data)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: { code?: string } } } }
      const code = ax?.response?.data?.detail?.code
      if (code === 'LLM_CATALOG_PAYLOAD_TOO_LARGE') setError(lc.errPayload)
      else if (code === 'LLM_CATALOG_UNKNOWN_MODEL') setError(lc.errUnknownModel)
      else if (code === 'LLM_CATALOG_FORBIDDEN') setError(lc.errForbidden)
      else setError(lc.saveError)
    } finally {
      setSaving(false)
    }
  }

  if (!Number.isFinite(wid)) {
    return <p className="p-6 text-sm text-red-600">{lc.loadError}</p>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-zinc-500">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-sm">{t.agents.modelPicker.loading}</span>
      </div>
    )
  }

  if (!snapshot) {
    return <p className="p-6 text-sm text-red-600">{error || lc.loadError}</p>
  }

  return (
    <div className="px-6 py-8 max-w-4xl">
      <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">{lc.title}</h2>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{lc.subtitle}</p>
      <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-500">{lc.byokNote}</p>

      {error && (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="mt-8 space-y-10">
        {snapshot.providers.map(prov => (
          <section key={prov.id}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                {prov.id}
              </h3>
              {prov.has_api_key === false && (prov.id === 'openai' || prov.id === 'anthropic') && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  {lc.noKeyBadge}
                </span>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto rounded-3xl border border-zinc-200/80 dark:border-zinc-800">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-zinc-50 dark:bg-zinc-900">
                  <tr className="border-b border-zinc-200 dark:border-zinc-800">
                    <th className="px-4 py-2 font-medium text-zinc-700 dark:text-zinc-300">{lc.colModel}</th>
                    <th className="w-24 px-2 py-2 font-medium text-zinc-700 dark:text-zinc-300">{lc.sortLabel}</th>
                    <th className="w-28 px-2 py-2 font-medium text-zinc-700 dark:text-zinc-300">{lc.enabledLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {prov.models.map(m => (
                    <tr
                      key={`${prov.id}:${m.id}`}
                      className="border-b border-zinc-100 dark:border-zinc-800/80"
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium text-zinc-900 dark:text-zinc-100">{m.id}</div>
                        {m.description ? (
                          <div className="text-xs text-zinc-500 dark:text-zinc-400">{m.description}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          className="w-full rounded-full border border-zinc-200 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
                          value={m.sort_order}
                          onChange={e =>
                            updateModel(prov.id, m.id, {
                              sort_order: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={m.enabled}
                          onChange={e => updateModel(prov.id, m.id, { enabled: e.target.checked })}
                          className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>

      <div className="mt-10 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-full bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-600 disabled:opacity-50"
        >
          {saving ? lc.saving : lc.save}
        </button>
      </div>
    </div>
  )
}
